/* ARRAY - "Best Sellers". Paging, the roving outline, arrows, timer bar, add
   to bag.

   The section renders EVERY product up front, two "faces" stacked in each of
   the four columns. Nothing is built, fetched or re-ordered here: paging only
   moves an `is-on` class and re-points one custom property, so the browser can
   run the cross-fade on the compositor. That is why the transition is smooth.

   ---------------------------------------------------------------------------
   ONE BEAT, NEVER PAUSED, STARTING ON ARRIVAL (24 Sep 2026). The client: "this
   section should auto scroll one to another with equal interval of 3 seconds
   in a loop, and no matter where the cursor is it should keep scrolling - but
   right after someone arrives at this section."

   EQUAL INTERVAL: paging used to run on its own 6s clock (`data-interval`)
   while the outline moved every 3s (`data-feat-ms`), so the row changed at two
   rhythms at once. Paging now uses the outline's interval, so both move every
   3s. `data-interval` is only a fallback for when the outline is switched off.
   That is done here rather than by changing the section's setting because a
   value saved in the theme editor would silently override a new default.

   NEVER PAUSED: paging used to hold while the pointer or keyboard focus was
   inside the section. Both holds are gone - the row keeps moving wherever the
   cursor is. The timer bar still restarts from zero when an arrow is used.

   ON ARRIVAL: an IntersectionObserver gates both clocks. `live` is false until
   the row overlaps the middle of the viewport, so a visitor meets page one and
   the first outline exactly as rendered. Scrolling away stops both clocks and
   CANCELS the bar, so coming back starts a whole fresh interval.
   ---------------------------------------------------------------------------

   THE TICK'S COLOUR IS SET IN JS, THE NUMERAL'S IS NOT. The tick belongs to the
   COLUMN, which survives a page change, so its colour is one `--tint` per card,
   transitioned in CSS and registered with @property so it can interpolate. The
   numeral is inline per face so the two numerals hold their own tints as they
   fade past each other.

   THE TIMER BAR IS A WEB ANIMATION, not a CSS animation, so it can restart from
   zero the instant an arrow is used.

   ADD TO BAG uses Horizon's contract, copied from beyondads-bag.js: POST
   /cart/add.js asking for the cart-items sections by id, then a `cart:update`
   CustomEvent carrying the markup Shopify returned. Skip the sections and the
   header count does not move until the next page load. ONE REQUEST CARRIES A
   MULTI-ITEM ADD - separate POSTs would race for the cart's lock.
*/
(function () {
  'use strict';

  var ROOT = '[data-gt-root]';

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  function sectionIds() {
    var ids = [], c = document.querySelectorAll('cart-items-component'), i;
    for (i = 0; i < c.length; i++) {
      if (c[i].dataset && c[i].dataset.sectionId) ids.push(c[i].dataset.sectionId);
    }
    return ids;
  }

  function announce(productId, qty, sections) {
    var ev;
    try {
      ev = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: {},
          sourceId: 'beyondads-bestsellers',
          data: {
            source: 'product-form-component',
            itemCount: qty,
            productId: productId,
            sections: sections || {}
          }
        }
      });
    } catch (e) { return; }
    document.dispatchEvent(ev);
  }

  function post(btn, items, pid) {
    if (btn.disabled || btn.__busy || !items.length) return;

    btn.__busy = true;
    btn.classList.add('is-busy');

    var body = { items: items };
    var ids = sectionIds();
    if (ids.length) {
      body.sections = ids.join(',');
      body.sections_url = window.location.pathname;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        btn.classList.remove('is-busy');
        if (!res || res.status >= 400 || res.errors) { btn.__busy = false; return; }
        btn.classList.add('is-done');
        announce(pid, items.length, res.sections);
        setTimeout(function () {
          btn.classList.remove('is-done');
          btn.__busy = false;
        }, 1700);
      })
      .catch(function () {
        btn.classList.remove('is-busy');
        btn.__busy = false;
      });
  }

  function addOne(btn) {
    var id = parseInt(btn.getAttribute('data-vid'), 10);
    if (!id) return;
    post(btn, [{ id: id, quantity: 1 }], btn.getAttribute('data-pid'));
  }

  function addAll(btn) {
    var raw = (btn.getAttribute('data-ids') || '').split(',');
    var items = [], i, n;
    for (i = 0; i < raw.length; i++) {
      n = parseInt(raw[i], 10);
      if (n) items.push({ id: n, quantity: 1 });
    }
    post(btn, items, null);
  }

  function build(root) {
    if (root.__gt) return;
    root.__gt = true;

    var cards = [].slice.call(root.querySelectorAll('[data-gt-card]'));
    var ffaces = [].slice.call(root.querySelectorAll('[data-gt-fface]'));
    var count = root.querySelector('[data-gt-count]');
    var bar = root.querySelector('[data-gt-prog]');
    var pages = parseInt(root.getAttribute('data-pages'), 10) || 1;
    var per = parseInt(root.getAttribute('data-per'), 10) || 4;
    var total = parseInt(root.getAttribute('data-total'), 10) || per;
    var featMs = parseInt(root.getAttribute('data-feat-ms'), 10) || 0;
    /* one beat: paging follows the outline - see the note at the top */
    var wait = featMs || parseInt(root.getAttribute('data-interval'), 10) || 3000;

    var page = 0, timer = null, anim = null;
    var featTimer = null, featAt = 0, live = false;

    function paint() {
      var i, j, k, faces, metas, face, links, m;
      for (i = 0; i < cards.length; i++) {
        faces = cards[i].querySelectorAll('[data-gt-face]');
        metas = cards[i].querySelectorAll('[data-gt-mface]');
        if (!faces.length) continue;
        k = page < faces.length ? page : faces.length - 1;

        for (j = 0; j < faces.length; j++) {
          faces[j].classList.toggle('is-on', j === k);
          faces[j].setAttribute('aria-hidden', j === k ? 'false' : 'true');
          faces[j].tabIndex = j === k ? 0 : -1;
        }
        for (j = 0; j < metas.length; j++) {
          metas[j].classList.toggle('is-on', j === k);
          metas[j].setAttribute('aria-hidden', j === k ? 'false' : 'true');
          links = metas[j].querySelectorAll('a,button');
          for (m = 0; m < links.length; m++) links[m].tabIndex = j === k ? 0 : -1;
        }

        face = faces[k];
        if (face) cards[i].style.setProperty('--tint', face.getAttribute('data-tint') || '#E8DCD6');
      }

      for (i = 0; i < ffaces.length; i++) {
        ffaces[i].classList.toggle('is-on', i === page);
        ffaces[i].setAttribute('aria-hidden', i === page ? 'false' : 'true');
        links = ffaces[i].querySelectorAll('a,button');
        for (m = 0; m < links.length; m++) links[m].tabIndex = i === page ? 0 : -1;
      }

      if (count) {
        var a = page * per + 1;
        var b = Math.min(page * per + per, total);
        count.textContent = pad(a) + ' — ' + pad(b);
      }
    }

    /* ---- the roving outline ---- */
    function startFeature() {
      if (!featMs || cards.length < 2 || featTimer) return;
      var i;
      for (i = 0; i < cards.length; i++) {
        if (cards[i].classList.contains('is-feat')) { featAt = i; break; }
      }
      featTimer = setInterval(function () {
        featAt = (featAt + 1) % cards.length;
        for (var j = 0; j < cards.length; j++) {
          var on = j === featAt;
          cards[j].classList.toggle('is-feat', on);
          var tag = cards[j].querySelector('.gt-tag');
          if (tag) tag.setAttribute('aria-hidden', on ? 'false' : 'true');
        }
      }, featMs);
    }
    function stopFeature() {
      if (featTimer) { clearInterval(featTimer); featTimer = null; }
    }

    function runBar() {
      if (!bar || !bar.animate) return;
      if (anim) { anim.cancel(); anim = null; }
      if (pages < 2) return;
      anim = bar.animate(
        [{ transform: 'scaleX(0)' }, { transform: 'scaleX(1)' }],
        { duration: wait, easing: 'linear', fill: 'forwards' }
      );
    }

    function stop() { if (timer) { clearInterval(timer); timer = null; } }

    function start() {
      stop();
      if (!live) return;
      runBar();
      if (pages > 1 && !document.hidden) {
        timer = setInterval(function () { page = (page + 1) % pages; paint(); runBar(); }, wait);
      }
    }

    function go(dir) {
      page = (page + dir + pages) % pages;
      paint();
      start();
    }

    /* ---- the viewport gate ---- */
    function wake() {
      if (live) return;
      live = true;
      start();
      startFeature();
    }
    function sleep() {
      if (!live) return;
      live = false;
      stop();
      stopFeature();
      if (anim) { anim.cancel(); anim = null; }
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { stop(); if (anim) anim.pause(); }
      else if (live) start();
    });

    root.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var prev = e.target.closest('[data-gt-prev]');
      var next = e.target.closest('[data-gt-next]');
      var one = e.target.closest('[data-gt-add]');
      var all = e.target.closest('[data-gt-all]');
      if (prev && root.contains(prev)) { e.preventDefault(); go(-1); return; }
      if (next && root.contains(next)) { e.preventDefault(); go(1); return; }
      if (one && root.contains(one)) { e.preventDefault(); e.stopPropagation(); addOne(one); return; }
      if (all && root.contains(all)) { e.preventDefault(); e.stopPropagation(); addAll(all); }
    });

    paint();

    if (typeof IntersectionObserver === 'function') {
      new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) wake(); else sleep();
        }
      }, { rootMargin: '-12% 0px -12% 0px', threshold: 0 }).observe(root);
    } else {
      wake();
    }
  }

  function boot() {
    var list = document.querySelectorAll(ROOT), i;
    for (i = 0; i < list.length; i++) build(list[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();